// Vitals and their trends.
//
// A compact table plus tiny sparklines for "what is it now, and which way is
// it going", and — above them — the shared VitalsTrendChart (KMC #34) for the
// in-stay trend at true time-taken positions, with the hospital's own
// configured NEWS2 lines for adults only.
//
// Nursing vitals carry two timestamps (KMC #36): TIME TAKEN (recorded_at —
// what everything plots and alerts on) and TIME ENTERED (entered_at, server
// clock). A reading entered beyond the hospital's lag policy needs a reason
// and is marked "Late entry" here and hollow on the chart. A saved reading is
// never edited: "Amend" creates a new version with a mandatory reason, and
// "Show amendment history" brings the superseded originals back into view.
//
// The 24h default is the performance rule, not a UI preference: a three-week
// stay would otherwise ship every observation to draw one screen.
import { Fragment, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import VoiceFormFiller from '@/components/VoiceFormFiller';
import { appendText, numField, selectField, textField, vNum, vOneOf, vText, tempToC, type Extracted } from '@/lib/voiceFill';
import { VitalsTrendChart } from '@/components/vitals/VitalsTrendChart';
import { ApiError } from '@/lib/api/client';
import { ADULT_MIN_AGE, ipReferenceLines, isAdult } from '@/lib/vitalReferenceBands';
import { useAlertRules, useAmendVitals, useCaseSheet, useRecordVitals, useVitals } from '../hooks';
import type { Vitals } from '../types';
import { Section, Empty, fmtDateTime, useCan, BackdateField } from '../ui';

const WINDOWS = [
  { label: '24 hours', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: 'Admission to date', hours: null },
];

/** Values a sparkline is drawn for, and which direction reads as improvement. */
const TRACKS: { key: keyof Vitals; label: string; unit: string }[] = [
  { key: 'bp_systolic', label: 'SBP', unit: '' },
  { key: 'pulse_rate', label: 'Pulse', unit: '' },
  { key: 'respiratory_rate', label: 'RR', unit: '' },
  { key: 'spo2', label: 'SpO2', unit: '%' },
  { key: 'temperature', label: 'Temp', unit: '°C' },
  { key: 'pain_score', label: 'Pain', unit: '/10' },
];

/** Every clinical value the form edits, in display order. */
const VALUE_FIELDS: { key: keyof Vitals; label: string }[] = [
  { key: 'bp_systolic', label: 'Systolic' },
  { key: 'bp_diastolic', label: 'Diastolic' },
  { key: 'pulse_rate', label: 'Pulse' },
  { key: 'respiratory_rate', label: 'Resp. rate' },
  { key: 'temperature', label: 'Temp °C' },
  { key: 'spo2', label: 'SpO2 %' },
  { key: 'pain_score', label: 'Pain 0-10' },
  { key: 'weight_kg', label: 'Weight kg' },
];
const NUMERIC_KEYS = [...VALUE_FIELDS.map(f => f.key), 'oxygen_flow_lpm'] as (keyof Vitals)[];
const OXYGEN_MODES = ['room-air', 'nasal-cannula', 'face-mask', 'nrbm', 'venturi', 'hfnc', 'niv', 'ventilator'] as const;

// One dictation fills the whole set of readings — a nurse at the bedside says
// "BP 130 by 80, pulse 92, temp 37.8, sats 96 on room air, RR 18, pain 3"
// once, rather than tapping eight boxes. Readings land in the form for
// review; nothing is saved until Save vitals.
const VITALS_VOICE_FIELDS = [
  numField('bp_systolic', 'Blood pressure — systolic (the upper/first number)'),
  numField('bp_diastolic', 'Blood pressure — diastolic (the lower/second number)'),
  numField('pulse_rate', 'Pulse / heart rate per minute'),
  numField('respiratory_rate', 'Respiratory rate per minute'),
  numField('temperature', 'Temperature (number as spoken)'),
  numField('spo2', 'SpO2 / oxygen saturation percent'),
  numField('pain_score', 'Pain score 0-10'),
  numField('weight_kg', 'Weight in kg'),
  selectField('oxygen_delivery', 'Oxygen delivery mode, only if mentioned', OXYGEN_MODES),
  numField('oxygen_flow_lpm', 'Oxygen flow in litres per minute'),
  textField('symptoms', 'Observations — anything else noted about the patient'),
];


/**
 * A 40px inline trend. Deliberately unlabelled and unaxised — it conveys
 * direction and volatility, and the exact numbers are in the table beside it.
 * Anything more would be a chart pretending a 40px box can be read precisely.
 */
function Spark({ values }: { values: number[] }) {
  if (values.length < 2) return <span className="text-ink-muted">—</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const w = 64, h = 18;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / span) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5"
        className="text-teal" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** The hospital's entry-lag policy in minutes, or null when not enforced —
 * a client-side HINT only; the server re-checks and is the authority. */
function useEntryLagMinutes(): number | null {
  const { data } = useAlertRules();
  const rule = data?.find(r => r.rule_key === 'vitals_entry_lag');
  if (!rule || !rule.enabled) return null;
  const n = Number(rule.params?.max_lag_minutes);
  return Number.isFinite(n) ? n : null;
}

const toStr = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/**
 * Shared by record and amend: the value grid, time taken, and error display.
 * `initial` pre-fills an amendment from the reading being corrected.
 */
function VitalsForm({ admissionId, initial, onDone }: {
  admissionId: number;
  initial?: Vitals;
  onDone: () => void;
}) {
  const amending = !!initial;
  const record = useRecordVitals(admissionId);
  const amend = useAmendVitals(admissionId);
  const lagLimit = useEntryLagMinutes();

  const [f, setF] = useState<Record<string, string>>(() => {
    if (!initial) return { oxygen_delivery: 'room-air' };
    const out: Record<string, string> = {};
    for (const k of NUMERIC_KEYS) out[k] = toStr(initial[k]);
    out.oxygen_delivery = initial.oxygen_delivery || 'room-air';
    out.symptoms = initial.symptoms || '';
    return out;
  });
  const [takenAt, setTakenAt] = useState<string | undefined>(initial?.recorded_at);
  const [lateReason, setLateReason] = useState('');
  const [amendReason, setAmendReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }));
  const num = (k: string) => (f[k] ? Number(f[k]) : undefined);

  // Evaluated at render: minutes between time taken and now. Only drives
  // whether the reason box is shown up-front; the server decides.
  const lagMinutes = takenAt ? Math.floor((Date.now() - new Date(takenAt).getTime()) / 60000) : 0;
  const looksLate = !amending && lagLimit != null && lagMinutes > lagLimit;
  const needsLateReason = looksLate || !!fieldErr.late_entry_reason;

  const onError = (e: unknown) => {
    if (e instanceof ApiError) {
      setFieldErr(e.fieldErrors);
      setErr(e.message);
    } else setErr('Could not save vitals.');
  };

  const submit = () => {
    setErr(null);
    setFieldErr({});
    if (!amending) {
      record.mutate({
        recorded_at: takenAt,
        bp_systolic: num('bp_systolic'), bp_diastolic: num('bp_diastolic'),
        pulse_rate: num('pulse_rate'), respiratory_rate: num('respiratory_rate'),
        temperature: num('temperature'), spo2: num('spo2'), pain_score: num('pain_score'),
        oxygen_flow_lpm: num('oxygen_flow_lpm'),
        oxygen_delivery: f.oxygen_delivery || undefined,
        weight_kg: num('weight_kg'),
        symptoms: f.symptoms || undefined,
        late_entry_reason: needsLateReason ? lateReason : undefined,
      } as Partial<Vitals>, { onSuccess: onDone, onError });
      return;
    }
    if (!amendReason.trim()) {
      setFieldErr({ amendment_reason: 'Say why this reading is being corrected.' });
      return;
    }
    // Send only what changed; the server carries the rest forward.
    const changes: Record<string, unknown> = {};
    for (const k of NUMERIC_KEYS) {
      if (toStr(initial![k]) !== (f[k] ?? '') && !(Number(initial![k]) === Number(f[k]) && f[k] !== '')) {
        changes[k] = f[k] === '' ? '' : Number(f[k]);
      }
    }
    if ((initial!.oxygen_delivery || 'room-air') !== f.oxygen_delivery) changes.oxygen_delivery = f.oxygen_delivery;
    if ((initial!.symptoms || '') !== (f.symptoms || '')) changes.symptoms = f.symptoms;
    if (takenAt && new Date(takenAt).getTime() !== new Date(initial!.recorded_at).getTime()) changes.recorded_at = takenAt;
    amend.mutate({ vitalsId: initial!.id, reason: amendReason.trim(), changes: changes as Partial<Vitals> },
      { onSuccess: onDone, onError });
  };

  const applyVoice = (x: Extracted) => {
    setF(p => {
      const next = { ...p };
      for (const k of NUMERIC_KEYS) {
        const n = vNum(x, k);
        if (n !== undefined) next[k] = String(k === 'temperature' ? tempToC(n) : n);
      }
      const o2 = vOneOf(x, 'oxygen_delivery', OXYGEN_MODES);
      if (o2) next.oxygen_delivery = o2;
      const obs = vText(x, 'symptoms');
      if (obs) next.symptoms = appendText(p.symptoms, obs, '; ');
      return next;
    });
  };

  const field = (k: string, label: string) => (
    <div key={k}>
      <label className="mb-1 block text-xs font-semibold">{label}</label>
      <Input value={f[k] ?? ''} onChange={e => set(k, e.target.value)} inputMode="decimal" />
    </div>
  );

  return (
    <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3">
      {amending && (
        <div className="mb-2 text-[12px] text-ink-muted">
          Amending the reading taken {fmtDateTime(initial!.recorded_at)}. The original stays on record; this saves a new version.
        </div>
      )}
      <VoiceFormFiller fields={VITALS_VOICE_FIELDS} onFields={applyVoice}
        label={amending ? '🎙 Dictate corrected readings' : '🎙 Dictate vitals'}
        hint="BP 130 by 80, pulse 92, temp 37.8, sats 96 on room air, RR 18, pain 3" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {VALUE_FIELDS.map(v => field(v.key, v.label))}
        <div>
          <label className="mb-1 block text-xs font-semibold">Oxygen</label>
          <NativeSelect value={f.oxygen_delivery ?? 'room-air'} onChange={e => set('oxygen_delivery', e.target.value)}>
            {OXYGEN_MODES.map(o => <option key={o} value={o}>{o}</option>)}
          </NativeSelect>
        </div>
        {f.oxygen_delivery !== 'room-air' && field('oxygen_flow_lpm', 'O2 L/min')}
        <div className="col-span-2 sm:col-span-4">
          <label className="mb-1 block text-xs font-semibold">Observations</label>
          <Input value={f.symptoms ?? ''} onChange={e => set('symptoms', e.target.value)} />
        </div>
      </div>

      <div className="mt-2">
        <BackdateField value={takenAt} onChange={setTakenAt} label="Time taken" />
        {fieldErr.recorded_at && <div className="mt-1 text-[12px] text-danger-ink">{fieldErr.recorded_at}</div>}
        {!amending && (
          <div className="mt-1 text-[11px] text-ink-muted">
            Time entered is recorded automatically when you save.
            {lagLimit != null && <> Readings entered more than {lagLimit} min after they were taken need a reason.</>}
          </div>
        )}
      </div>

      {needsLateReason && (
        <div className="mt-2">
          <label className="mb-1 block text-xs font-semibold">
            Late entry reason <span className="font-normal text-ink-muted">— taken {lagMinutes} min ago</span>
          </label>
          <Textarea rows={2} value={lateReason} onChange={e => setLateReason(e.target.value)}
            invalid={!!fieldErr.late_entry_reason}
            placeholder="e.g. Emergency on the ward; observations charted after stabilising the patient" />
        </div>
      )}

      {amending && (
        <div className="mt-2">
          <label className="mb-1 block text-xs font-semibold">Reason for amendment (required)</label>
          <Textarea rows={2} value={amendReason} onChange={e => setAmendReason(e.target.value)}
            invalid={!!fieldErr.amendment_reason} placeholder="e.g. Pulse misread from the monitor" />
        </div>
      )}

      {err && <div className="mt-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={record.isPending || amend.isPending}>
          {amending ? 'Save amendment' : 'Save vitals'}
        </Button>
      </div>
    </div>
  );
}

/** "Taken 10:15" plus, when it matters, "Entered 10:32 · Late entry". */
function TakenCell({ r }: { r: Vitals }) {
  const enteredGap = r.entered_at ? new Date(r.entered_at).getTime() - new Date(r.recorded_at).getTime() : 0;
  return (
    <div>
      <div className="tabular-nums">{fmtDateTime(r.recorded_at)}</div>
      {r.entered_at && (r.late_entry || enteredGap > 60_000) && (
        <div className="text-[10.5px] text-ink-muted" title={r.late_entry_reason || undefined}>
          Entered {fmtDateTime(r.entered_at)}
          {r.late_entry && <span className="font-semibold text-warn-ink"> · Late entry</span>}
        </div>
      )}
    </div>
  );
}

export function VitalsSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const [windowIdx, setWindowIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [amendingRow, setAmendingRow] = useState<Vitals | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showChart, setShowChart] = useState(true);
  const hours = WINDOWS[windowIdx]?.hours ?? null;
  // Memoized on (admissionId, hours) only — recomputing this on every render
  // (as Date.now() would) mints a new query key each time, which starves the
  // list into a permanent "loading" state under constant refetch pressure.
  // "Admission to date" (hours === null) deliberately sends no `from` at
  // all — the backend then treats that as truly unbounded rather than its
  // usual "default to the last 24h", since an intake reading recorded a few
  // minutes before admission (visit-linked, not admission-linked) would
  // otherwise clip out right at the boundary that matters most.
  const from = useMemo(
    () => (hours ? new Date(Date.now() - hours * 3600000).toISOString() : undefined),
    [admissionId, hours],
  );
  const { data, isLoading } = useVitals(admissionId, from, true, showHistory);
  // Already cached by the workspace's first paint — no extra request.
  const { data: sheet } = useCaseSheet(admissionId);
  const { data: rules } = useAlertRules();

  const canRecord = can('ip:vitals') && !readOnly;
  const canAmend = can('ip:vitals-amend') && !readOnly;
  const all = data ?? [];
  // The chart, sparklines and latest values only ever use CURRENT versions.
  const rows = all.filter(r => !r.superseded_by_vitals_id);
  // Oldest→newest for the sparkline; the table stays newest-first.
  const chrono = [...rows].reverse();

  const adult = isAdult(sheet?.patient.age, sheet?.patient.dob);
  const referenceNote = !sheet ? undefined
    : adult ? 'Dashed lines: this hospital’s configured NEWS2 alert thresholds (adult).'
    : `No reference lines: the configured thresholds are adult-only, and this patient is under ${ADULT_MIN_AGE} or has no recorded age.`;

  return (
    <Section
      focusId="vitals"
      title="Vitals"
      subtitle={`${rows.length} observation${rows.length === 1 ? '' : 's'} · ${WINDOWS[windowIdx]?.label}`}
      action={
        <>
          <NativeSelect
            className="h-7 w-auto text-xs"
            value={String(windowIdx)}
            onChange={e => setWindowIdx(Number(e.target.value))}
          >
            {WINDOWS.map((w, i) => <option key={w.label} value={i}>{w.label}</option>)}
          </NativeSelect>
          {canRecord && !recording && !amendingRow && (
            <Button size="sm" data-focus="vitals:new" onClick={() => setRecording(true)}>+ Vitals</Button>
          )}
        </>
      }
    >
      {recording && <VitalsForm admissionId={admissionId} onDone={() => setRecording(false)} />}
      {amendingRow && (
        <VitalsForm key={amendingRow.id} admissionId={admissionId} initial={amendingRow} onDone={() => setAmendingRow(null)} />
      )}

      {isLoading ? <LoadingState label="Loading vitals…" />
        : !rows.length ? <Empty label="No vitals recorded in this window" />
        : (
          <>
            <div className="mb-3 flex flex-wrap gap-4">
              {TRACKS.map(t => {
                const vals = chrono
                  .map(r => Number(r[t.key]))
                  .filter(v => Number.isFinite(v));
                const latest = vals[vals.length - 1];
                return (
                  <div key={String(t.key)} className="min-w-[92px]">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{t.label}</div>
                    <div className="text-sm font-semibold tabular-nums">
                      {latest !== undefined ? `${latest}${t.unit}` : '—'}
                    </div>
                    <Spark values={vals} />
                  </div>
                );
              })}
            </div>

            <div className="mb-3 rounded-lg border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">In-stay trend</div>
                <button type="button" onClick={() => setShowChart(v => !v)}
                  className="text-[11px] font-semibold text-teal underline-offset-2 hover:underline">
                  {showChart ? 'Hide chart' : 'Show chart'}
                </button>
              </div>
              {showChart && (
                <VitalsTrendChart
                  readings={rows}
                  tempUnit="C"
                  referenceLines={m => (adult ? ipReferenceLines(rules, m, 'C') : [])}
                  referenceNote={referenceNote}
                  initialMetric="bp"
                />
              )}
            </div>

            <div className="mb-1 flex justify-end">
              <label className="flex items-center gap-1.5 text-[11.5px] text-ink-muted">
                <input type="checkbox" checked={showHistory} onChange={e => setShowHistory(e.target.checked)} />
                Show amendment history
              </label>
            </div>

            <div className="-mx-1 overflow-x-auto px-1">
              <table className="w-full min-w-[700px] text-[12.5px]">
                <thead>
                  <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-ink-muted">
                    <th className="py-1.5 pr-3 font-semibold">Time taken</th>
                    <th className="py-1.5 pr-3 font-semibold">BP</th>
                    <th className="py-1.5 pr-3 font-semibold">Pulse</th>
                    <th className="py-1.5 pr-3 font-semibold">RR</th>
                    <th className="py-1.5 pr-3 font-semibold">Temp</th>
                    <th className="py-1.5 pr-3 font-semibold">SpO2</th>
                    <th className="py-1.5 pr-3 font-semibold">O2</th>
                    <th className="py-1.5 pr-3 font-semibold">Pain</th>
                    <th className="py-1.5 pr-3 font-semibold">By</th>
                    <th className="py-1.5 font-semibold" />
                  </tr>
                </thead>
                <tbody>
                  {(showHistory ? all : rows).map(r => {
                    const superseded = !!r.superseded_by_vitals_id;
                    const amendable = canAmend && !superseded && r.admission_id === admissionId && !amendingRow && !recording;
                    const hasReasonRow = (r.version ?? 1) > 1 && !!r.amendment_reason;
                    return (
                      <Fragment key={r.id}>
                      <tr data-focus={`vitals:${r.id}`} className={`${hasReasonRow ? '' : 'border-b border-border/60 last:border-0'} ${superseded ? 'text-ink-muted line-through decoration-ink-muted/60' : ''}`}>
                        <td className="py-1.5 pr-3 align-top whitespace-nowrap"><TakenCell r={r} /></td>
                        <td className="py-1.5 pr-3 align-top tabular-nums">
                          {r.bp_systolic != null ? `${r.bp_systolic}/${r.bp_diastolic ?? '—'}` : '—'}
                        </td>
                        <td className="py-1.5 pr-3 align-top tabular-nums">{r.pulse_rate ?? '—'}</td>
                        <td className="py-1.5 pr-3 align-top tabular-nums">{r.respiratory_rate ?? '—'}</td>
                        <td className="py-1.5 pr-3 align-top tabular-nums">
                          {r.temperature ?? '—'}
                          {r.temperature_recorded_f != null && (
                            <div className="text-[10.5px] text-ink-muted" title="Taken at the OPD Vitals stage before admission, in °F; shown here in °C">
                              ({r.temperature_recorded_f} °F at OPD)
                            </div>
                          )}
                        </td>
                        <td className="py-1.5 pr-3 align-top tabular-nums">{r.spo2 ?? '—'}</td>
                        <td className="py-1.5 pr-3 align-top">
                          {r.oxygen_delivery
                            ? `${r.oxygen_delivery}${r.oxygen_flow_lpm ? ` ${r.oxygen_flow_lpm}L` : ''}`
                            : '—'}
                        </td>
                        <td className="py-1.5 pr-3 align-top tabular-nums">{r.pain_score ?? '—'}</td>
                        <td className="py-1.5 pr-3 align-top text-ink-muted">{r.recorded_by_name || r.recorded_by || '—'}</td>
                        <td className="py-1.5 align-top text-right whitespace-nowrap no-underline">
                          {superseded && <Badge variant="neutral">Superseded</Badge>}
                          {(r.version ?? 1) > 1 && !superseded && (
                            <Badge variant="info">Amended v{r.version}</Badge>
                          )}
                          {amendable && (
                            <button type="button" onClick={() => setAmendingRow(r)}
                              className="ml-2 text-[11.5px] font-semibold text-teal underline-offset-2 hover:underline">
                              Amend
                            </button>
                          )}
                        </td>
                      </tr>
                      {/* Printed, not a hover tooltip — a tablet has no hover,
                          and the reason is part of the clinical record. */}
                      {hasReasonRow && (
                        <tr className="border-b border-border/60 last:border-0">
                          <td colSpan={10} className="pb-1.5 pr-3 text-[11.5px] text-ink-muted">
                            Amendment reason: {r.amendment_reason}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
    </Section>
  );
}
